import { useOptionalBridge } from '@web/adapter/useBridgeRuntime'
import type { RelatedSession } from '@web/api/types'
import { openSessionArtifact } from '@web/components/SessionArtifacts'
import { SessionLink } from '@web/components/SessionLinks'
import {
  isDataObject,
  readableResult,
} from '@web/components/tools/StructuredContent'
import { Icon } from '@web/components/ui/icons'

/** Render only host-owned artifact/session references; arbitrary result URLs remain plain data. */
export function ToolResultLinks({ result }: { result: unknown }) {
  const bridge = useOptionalBridge()
  const parsed = readableResult(result)
  const data =
    isDataObject(parsed) && isDataObject(parsed.data) ? parsed.data : parsed
  if (!isDataObject(data) || !bridge) return null
  const candidates = [
    data.artifact,
    ...(Array.isArray(data.artifacts) ? data.artifacts : []),
  ]
  const artifacts = candidates
    .filter(isDataObject)
    .filter(
      (item, index, all) =>
        typeof item.id === 'string' &&
        item.session_id === bridge.sessionId &&
        Array.isArray(item.files) &&
        item.files.length > 0 &&
        all.findIndex((other) => other.id === item.id) === index,
    )
  const child = data.child_session ?? data.handoff_session
  const session: RelatedSession | null =
    isDataObject(child) &&
    typeof child.id === 'string' &&
    typeof child.app_id === 'string'
      ? {
          id: child.id,
          app_id: child.app_id,
          title:
            typeof child.title === 'string'
              ? child.title
              : 'Open related session',
          org_id: typeof child.org_id === 'string' ? child.org_id : null,
        }
      : null
  if (!artifacts.length && !session) return null
  return (
    <div className="tool-result-links">
      {artifacts.map((artifact) => (
        <button
          type="button"
          key={String(artifact.id)}
          className="btn btn-sm"
          onClick={() =>
            openSessionArtifact({
              sessionId: bridge.sessionId,
              artifactId: String(artifact.id),
            })
          }
        >
          <Icon name="fileText" size={13} />
          Preview{' '}
          {typeof artifact.title === 'string' ? artifact.title : 'artifact'}
        </button>
      ))}
      {session && (
        <SessionLink
          className="btn btn-sm tool-result-session-link"
          containerClassName="tool-result-session"
          session={session}
        >
          Open {session.title}
        </SessionLink>
      )}
    </div>
  )
}
